import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

export default function Investors() {
  const { isAdmin } = useAuth();
  const [investors, setInvestors] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showTxn, setShowTxn] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [txn, setTxn] = useState({ type: "add", amount: "", note: "" });

  const fetchData = async () => {
    const res = await axios.get(
      "https://vape-backend-1.onrender.com/api/investors",
    );
    setInvestors(res.data);
  };
  useEffect(() => {
    fetchData();
  }, []);

  const addInvestor = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await axios.post(
        "https://vape-backend-1.onrender.com/api/investors",
        form,
      );
      toast.success(isAdmin ? "Investor added!" : "Submitted for approval!");
      setShowAdd(false);
      setForm({ name: "", phone: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };

  const addTransaction = async () => {
    const amt = parseFloat(txn.amount);
    if (!amt || amt <= 0) return toast.error("Enter valid amount");
    try {
      await axios.post(
        `https://vape-backend-1.onrender.com/api/investors/${showTxn._id}/transaction`,
        {
          ...txn,
          amount: amt,
        },
      );
      toast.success(
        isAdmin ? "Transaction recorded!" : "Submitted for approval!",
      );
      setShowTxn(null);
      setTxn({ type: "add", amount: "", note: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };

  const deleteInvestor = async (id) => {
    if (!window.confirm("Delete this investor and all their records?")) return;
    try {
      await axios.delete(
        `https://vape-backend-1.onrender.com/api/investors/${id}`,
      );
      toast.success("Investor deleted");
      fetchData();
    } catch (err) {
      toast.error("Error deleting");
    }
  };

  const openTxn = (inv) => {
    setShowTxn(inv);
    setTxn({ type: "add", amount: "", note: "" });
  };

  const totalInvested = investors.reduce((s, i) => s + i.totalInvested, 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Investors</h1>
          <p className="page-subtitle">Track investments and transactions</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          + Add Investor
        </button>
      </div>

      {/* Summary stats */}
      <div className="stat-grid">
        <div className="stat-card cyan">
          <div className="stat-label">Total Invested</div>
          <div className="stat-value">৳{totalInvested.toLocaleString()}</div>
          <div className="stat-sub">সব investor মিলিয়ে</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">Total Investors</div>
          <div className="stat-value">{investors.length}</div>
          <div className="stat-sub">Active investors</div>
        </div>
      </div>

      {investors.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <div className="icon">💼</div>
            <p>No investors yet</p>
          </div>
        </div>
      )}

      {/* ── Desktop table ── */}
      {investors.length > 0 && (
        <div className="card hide-mobile" style={{ marginBottom: 16 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Investor</th>
                  <th>Phone</th>
                  <th>Total Invested</th>
                  <th>Transactions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {investors.map((inv) => (
                  <React.Fragment key={inv._id}>
                    <tr>
                      <td style={{ fontWeight: 600 }}>{inv.name}</td>
                      <td style={{ color: "var(--text-secondary)" }}>
                        {inv.phone || "—"}
                      </td>
                      <td
                        style={{
                          fontWeight: 700,
                          color:
                            inv.totalInvested >= 0
                              ? "var(--success)"
                              : "var(--danger)",
                        }}
                      >
                        ৳{inv.totalInvested.toLocaleString()}
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: "var(--accent)" }}
                          onClick={() =>
                            setExpandedId(
                              expandedId === inv._id ? null : inv._id,
                            )
                          }
                        >
                          {inv.transactions?.length || 0} records{" "}
                          {expandedId === inv._id ? "▲" : "▼"}
                        </button>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => openTxn(inv)}
                        >
                          + Transaction
                        </button>
                        {isAdmin && (
                          <button
                            className="btn btn-danger btn-sm"
                            style={{ marginLeft: 6 }}
                            onClick={() => deleteInvestor(inv._id)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Inline transaction history row */}
                    {expandedId === inv._id && inv.transactions?.length > 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          style={{
                            padding: 0,
                            background: "var(--bg-primary)",
                          }}
                        >
                          <div style={{ padding: "12px 16px" }}>
                            <div
                              style={{
                                fontSize: "0.78rem",
                                color: "var(--accent)",
                                marginBottom: 8,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Transaction History — {inv.name}
                            </div>
                            <table>
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th>Amount</th>
                                  <th>Note</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {inv.transactions.map((t, i) => (
                                  <tr key={i}>
                                    <td>
                                      <span
                                        className={`badge-status ${t.type === "add" ? "badge-approved" : "badge-rejected"}`}
                                      >
                                        {t.type === "add"
                                          ? "⬆ Added"
                                          : "⬇ Withdrew"}
                                      </span>
                                    </td>
                                    <td
                                      className={
                                        t.type === "add"
                                          ? "text-profit"
                                          : "text-loss"
                                      }
                                      style={{ fontWeight: 600 }}
                                    >
                                      ৳{t.amount.toLocaleString()}
                                    </td>
                                    <td
                                      style={{
                                        color: "var(--text-secondary)",
                                        fontSize: "0.82rem",
                                      }}
                                    >
                                      {t.note || "—"}
                                    </td>
                                    <td
                                      style={{
                                        color: "var(--text-muted)",
                                        fontSize: "0.78rem",
                                      }}
                                    >
                                      {new Date(t.date).toLocaleDateString(
                                        "en-BD",
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedId === inv._id &&
                      inv.transactions?.length === 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            style={{
                              textAlign: "center",
                              color: "var(--text-muted)",
                              padding: "12px",
                              background: "var(--bg-primary)",
                              fontSize: "0.83rem",
                            }}
                          >
                            No transactions yet
                          </td>
                        </tr>
                      )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Mobile cards ── */}
      <div className="show-mobile">
        {investors.map((inv) => (
          <div
            className="card"
            key={inv._id}
            style={{ marginBottom: 12, padding: 0, overflow: "hidden" }}
          >
            {/* Card header */}
            <div
              style={{
                padding: "14px 14px 12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: "1rem" }}>
                  {inv.name}
                </div>
                {inv.phone && (
                  <div
                    style={{
                      fontSize: "0.78rem",
                      color: "var(--text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    📞 {inv.phone}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color:
                      inv.totalInvested >= 0
                        ? "var(--success)"
                        : "var(--danger)",
                  }}
                >
                  ৳{inv.totalInvested.toLocaleString()}
                </div>
                <div
                  style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}
                >
                  Total Invested
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 8, padding: "0 14px 12px" }}>
              <button
                className="btn btn-primary btn-sm"
                style={{ flex: 1 }}
                onClick={() => openTxn(inv)}
              >
                + Transaction
              </button>
              <button
                className="btn btn-ghost btn-sm"
                style={{ flex: 1 }}
                onClick={() =>
                  setExpandedId(expandedId === inv._id ? null : inv._id)
                }
              >
                History ({inv.transactions?.length || 0}){" "}
                {expandedId === inv._id ? "▲" : "▼"}
              </button>
              {isAdmin && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => deleteInvestor(inv._id)}
                >
                  Del
                </button>
              )}
            </div>

            {/* Transaction history expandable */}
            {expandedId === inv._id && (
              <div
                style={{
                  borderTop: "1px solid var(--border)",
                  padding: "12px 14px",
                  background: "var(--bg-primary)",
                }}
              >
                {inv.transactions?.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      color: "var(--text-muted)",
                      padding: "12px 0",
                      fontSize: "0.83rem",
                    }}
                  >
                    No transactions yet
                  </div>
                ) : (
                  inv.transactions.map((t, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 0",
                        borderBottom:
                          i < inv.transactions.length - 1
                            ? "1px solid var(--border)"
                            : "none",
                      }}
                    >
                      <div>
                        <span
                          className={`badge-status ${t.type === "add" ? "badge-approved" : "badge-rejected"}`}
                        >
                          {t.type === "add" ? "⬆ Added" : "⬇ Withdrew"}
                        </span>
                        {t.note && (
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-muted)",
                              marginTop: 3,
                            }}
                          >
                            {t.note}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          className={
                            t.type === "add" ? "text-profit" : "text-loss"
                          }
                          style={{ fontWeight: 700 }}
                        >
                          {t.type === "add" ? "+" : "−"}৳
                          {t.amount.toLocaleString()}
                        </div>
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "var(--text-muted)",
                          }}
                        >
                          {new Date(t.date).toLocaleDateString("en-BD")}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Add Investor Modal ── */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add Investor</h3>
            <div className="form-group">
              <label>Full Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Investor name"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Phone (optional)</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="01XXXXXXXXX"
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={() => setShowAdd(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={addInvestor}>
                {isAdmin ? "Add Investor" : "Submit for Approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transaction Modal ── */}
      {showTxn && (
        <div className="modal-overlay" onClick={() => setShowTxn(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Transaction</h3>

            {/* Investor info */}
            <div
              style={{
                background: "var(--bg-primary)",
                borderRadius: 10,
                padding: "12px 14px",
                marginBottom: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontWeight: 700 }}>{showTxn.name}</div>
                {showTxn.phone && (
                  <div
                    style={{
                      fontSize: "0.78rem",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {showTxn.phone}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    color: "var(--text-muted)",
                    marginBottom: 2,
                  }}
                >
                  Current Balance
                </div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: "var(--accent)",
                  }}
                >
                  ৳{showTxn.totalInvested.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Type toggle */}
            <div className="form-group">
              <label>Transaction Type</label>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background:
                      txn.type === "add"
                        ? "rgba(0,245,160,0.15)"
                        : "transparent",
                    color:
                      txn.type === "add"
                        ? "var(--success)"
                        : "var(--text-secondary)",
                    border: `1px solid ${txn.type === "add" ? "var(--success)" : "var(--border)"}`,
                  }}
                  onClick={() => setTxn({ ...txn, type: "add" })}
                >
                  ⬆ Add Investment
                </button>
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background:
                      txn.type === "withdraw"
                        ? "rgba(255,77,109,0.15)"
                        : "transparent",
                    color:
                      txn.type === "withdraw"
                        ? "var(--danger)"
                        : "var(--text-secondary)",
                    border: `1px solid ${txn.type === "withdraw" ? "var(--danger)" : "var(--border)"}`,
                  }}
                  onClick={() => setTxn({ ...txn, type: "withdraw" })}
                >
                  ⬇ Withdraw
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Amount ৳</label>
              <input
                type="number"
                value={txn.amount}
                onChange={(e) => setTxn({ ...txn, amount: e.target.value })}
                placeholder="0"
                min="1"
                autoFocus
              />
            </div>

            {/* Preview */}
            {parseFloat(txn.amount) > 0 && (
              <div
                style={{
                  background: "var(--bg-primary)",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 14,
                  border: "1px solid var(--border-accent)",
                  fontSize: "0.85rem",
                }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span style={{ color: "var(--text-secondary)" }}>
                    New Balance:
                  </span>
                  <strong
                    style={{
                      color:
                        txn.type === "add" ? "var(--success)" : "var(--danger)",
                    }}
                  >
                    ৳
                    {(
                      showTxn.totalInvested +
                      (txn.type === "add" ? 1 : -1) * parseFloat(txn.amount)
                    ).toLocaleString()}
                  </strong>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Note (optional)</label>
              <input
                value={txn.note}
                onChange={(e) => setTxn({ ...txn, note: e.target.value })}
                placeholder="Reason, reference..."
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={() => setShowTxn(null)}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{
                  background:
                    txn.type === "add" ? "var(--success)" : "var(--danger)",
                  color: txn.type === "add" ? "#0a0d14" : "white",
                }}
                onClick={addTransaction}
              >
                {txn.type === "add" ? "⬆ Add" : "⬇ Withdraw"}
                {!isAdmin ? " (Submit)" : ""}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
